import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Dropdown } from 'react-bootstrap'
import { Button, Select, type SelectOption } from '@enamelai/design-system'
import { Icon as CalendarIcon } from '@enamelai/design-system/icons/Calendar'
import { Icon as RequestIcon } from '@enamelai/design-system/icons/Request'
import { Icon as StaffIcon } from '@enamelai/design-system/icons/Staff'
import {
  CalendarComponent,
  DateRangePickerComponent,
} from '@syncfusion/ej2-react-calendars'
import { DateRangePicker } from '@components/common'
import {
  getDefaultPeriodForLatestData,
  getReportsToolbarMenu,
  getReportsToolbarMenuItemLabel,
  isPeriodIdAllowedForLatestData,
  REPORTS_TOOLBAR_DEFAULT_SELECTION,
  ReportsToolbarMenuEntry,
} from '@constants/Reports/reportFilterToolbarMenus.constants'
import { ReportsFilterOption } from '@dataTypes/reports.type'
import { SNAPSHOT_TIME_FILTER_OPTION } from '@constants/Reports/reportsDefaultToolbar.constants'
import type { Options } from '@popperjs/core'
import { useReportManager } from '@hooks/reports/useReportManager'
import {
  AddViewButton,
  UpdateViewButton,
  DeleteViewButton,
  GeneratePDFButton,
} from './ReportManager'
import { getFullName } from '@utils/fullname.util'
import { toSentenceCase } from '@utils/textCase.util'
import ScheduleReportButton from './ReportManager/ScheduleReportButton.component'
import DownloadPDFButton from './ReportManager/DownloadPDFButton.component'
import type { ReportPayloadData } from '@services/reports/reportPayloadData'

const TOGGLE_CLASS_STRIP = new Set(['dropdown-toggle', 'btn', 'btn-light'])

export const convertProviderListToDropdownOptions = (
  providerList: any[],
): any[] => {
  return [
    {
      type: 'item',
      id: 'all-providers',
      label: 'All Providers',
    },
    {
      type: 'divider',
      id: 'div-providers-named',
    },
    ...providerList.map((provider) => ({
      type: 'item' as const,
      id: provider.provider_id,
      label: `Dr. ${getFullName(provider)}`,
    })),
  ]
}

const REPORTS_FILTER_DROPDOWN_POPPER: Partial<Options> = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: { rootBoundary: 'viewport' as const, padding: 8 },
    },
    {
      name: 'reportToolbarSecondLayoutPass',
      enabled: true,
      phase: 'write',
      fn: ({ state }) => state,
      effect: ({ instance }) => {
        let to: ReturnType<typeof setTimeout> | undefined
        const raf = requestAnimationFrame(() => {
          to = setTimeout(() => {
            void instance.update()
          }, 0)
        })
        return () => {
          cancelAnimationFrame(raf)
          if (to) clearTimeout(to)
        }
      },
    },
  ],
}

const ReportToolbarFilterDropdownToggle = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, className, ...props }, ref) => {
  const mergedClass = ['reports-patient-base-health__filter-btn', className]
    .filter(Boolean)
    .join(' ')
    .trim()
    .split(/\s+/)
    .filter((token) => token && !TOGGLE_CLASS_STRIP.has(token))
    .join(' ')

  return (
    <Button
      ref={ref}
      type="button"
      variant="secondary"
      size="sm"
      className={mergedClass}
      {...props}
    >
      {children}
    </Button>
  )
})
ReportToolbarFilterDropdownToggle.displayName =
  'ReportToolbarFilterDropdownToggle'

function buildToolbarSelection(
  filters: ReportsFilterOption[],
  initialSelectionByFilterId: Record<string, string> | undefined,
  controlledValue: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = controlledValue
    ? { ...controlledValue }
    : {}
  for (const f of filters) {
    out[f.id] =
      controlledValue?.[f.id] ??
      initialSelectionByFilterId?.[f.id] ??
      REPORTS_TOOLBAR_DEFAULT_SELECTION[f.id] ??
      f.id
  }
  return out
}

export { ReportToolbarFilterDropdownToggle, buildToolbarSelection }

const menuToSelectOptions = (
  menu: ReportsToolbarMenuEntry[],
  preserveNamedItemCase = false,
): SelectOption[] =>
  menu.flatMap((entry) =>
    entry.type === 'item'
      ? [
          {
            value: entry.id,
            label:
              preserveNamedItemCase && entry.id !== 'all-providers'
                ? entry.label
                : toSentenceCase(entry.label),
          },
        ]
      : [],
  )

type ReportToolbarSelectProps = {
  value?: string
  placeholder?: string
  options: SelectOption[]
  icon?: React.ReactNode
  className?: string
  disabled?: boolean
  onChange?: (value: string) => void
}

function ReportToolbarSelect({
  value,
  placeholder,
  options,
  icon,
  className = '',
  disabled = false,
  onChange,
}: ReportToolbarSelectProps): React.ReactElement {
  return (
    <div
      className={`reports-toolbar-select${
        icon ? ' reports-toolbar-select--with-icon' : ''
      } ${className}`.trim()}
    >
      {icon ? (
        <span className="reports-toolbar-select__leading-icon">{icon}</span>
      ) : null}
      <Select
        className="reports-toolbar-select__select"
        label=""
        value={value}
        placeholder={placeholder}
        options={options}
        disabled={disabled}
        onChange={(nextValue) => {
          if (typeof nextValue === 'string') onChange?.(nextValue)
        }}
      />
    </div>
  )
}

function isInAppReportBundlePath(pathname: string): boolean {
  if (
    !pathname.startsWith('/report/bundles/') ||
    pathname.endsWith('/preview')
  ) {
    return false
  }
  const segments = pathname
    .slice('/report/bundles/'.length)
    .split('/')
    .filter(Boolean)
  return segments.length >= 2
}

const COMPARISON_MODE_PREVIEW_PATH =
  '/report/bundles/patients/patient-base-health/preview' as const

export type ReportFilterToolbarSavedViewOption = { id: string; label: string }

type ReportFilterToolbarProps = {
  filters: ReportsFilterOption[]
  initialSelectionByFilterId?: Record<string, string>
  value?: Record<string, string>
  onValueChange?: (next: Record<string, string>) => void
  onFilterChange?: (payload: {
    filterId: string
    valueId: string
    label: string
  }) => void
  pageId?: string
  previewPath?: string
  onReportSaveViewActionChange?: (node: JSX.Element) => void
  onReportGeneratePDFActionChange?: (node: JSX.Element) => void
  onReportScheduleReportActionChange?: (node: JSX.Element) => void
  onReportDownloadPDFActionChange?: (node: JSX.Element) => void
  pdfDataScope?: 'all' | 'custom'
  pdfMetrics?: Record<string, boolean>
  onScopeAndMetricsChange?: (
    scope?: 'all' | 'custom',
    metrics?: Record<string, boolean>,
  ) => void
  customCategories?: Record<string, boolean>
  onCustomCategoriesChange?: (categories: Record<string, boolean>) => void
  customTitle?: string
  onCustomTitleChange?: (title: string) => void
  onReportFiltersAndMetricsChange?: ({
    filters,
    metrics,
  }: {
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  }) => void
  onReportPayloadDataChange?: (
    data: ReportPayloadData,
    loading: boolean,
    error: Error | null,
  ) => void
}

function getFilterIcon(filter: ReportsFilterOption): React.ReactNode {
  switch (filter.id) {
    case 'period-category':
      return (
        <RequestIcon
          className="reports-filter-toolbar__leading-icon"
          aria-hidden
        />
      )
    case 'period-preset':
      return (
        <CalendarIcon
          className="reports-filter-toolbar__leading-icon"
          aria-hidden
        />
      )
    case 'provider-filter':
      return (
        <StaffIcon
          className="reports-filter-toolbar__leading-icon"
          aria-hidden
        />
      )
    case 'snapshot-time':
      return <span className="mgc_time_line" aria-hidden />
    default:
      return null
  }
}

const THIS_MONTH_ID = 'period-preset' as const
const DATE_RANGE_VALUE_ID = 'date-range' as const

function toLocalYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseYmdToLocalDate(iso: string | undefined, fallback: Date): Date {
  if (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
    const d = new Date(iso.slice(0, 10) + 'T12:00:00')
    if (!Number.isNaN(d.getTime())) return d
  }
  return new Date(
    fallback.getFullYear(),
    fallback.getMonth(),
    fallback.getDate(),
    12,
    0,
    0,
    0,
  )
}

type SnapshotMonthDateFilterProps = {
  filterLabel: string
  icon: React.ReactNode
  snapshotAsOfIso: string
  onSnapshotAsOfChange: (d: Date) => void
}

function SnapshotMonthDateFilter({
  filterLabel,
  icon,
  snapshotAsOfIso,
  onSnapshotAsOfChange,
}: SnapshotMonthDateFilterProps): React.ReactElement {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  const snapDate = useMemo(
    () => parseYmdToLocalDate(snapshotAsOfIso || undefined, new Date()),
    [snapshotAsOfIso],
  )

  const monthLabel = useMemo(() => {
    if (Number.isNaN(snapDate.getTime())) return filterLabel
    return snapDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }, [filterLabel, snapDate])

  // Keep Bootstrap dropdown open while interacting with EJ2 calendar DOM
  useEffect(() => {
    if (!open) return
    const onDocMouseDown = (e: MouseEvent): void => {
      const t = e.target
      if (!(t instanceof Node)) return
      if (wrapRef.current?.contains(t)) return
      const el = t instanceof Element ? t : t.parentElement
      if (el?.closest('.e-calendar, .e-popup, .e-popup-open')) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    return () => document.removeEventListener('mousedown', onDocMouseDown, true)
  }, [open])

  const handleCalendarChange = useCallback(
    (e: { value?: Date | null }) => {
      if (!e.value) return
      const next = new Date(
        e.value.getFullYear(),
        e.value.getMonth(),
        e.value.getDate(),
        12,
        0,
        0,
        0,
      )
      onSnapshotAsOfChange(next)
      setOpen(false)
    },
    [onSnapshotAsOfChange],
  )

  return (
    <div
      className="reports-filter-toolbar-dropdown reports-filter-toolbar-dropdown--snapshot"
      ref={wrapRef}
    >
      <Dropdown
        className="reports-filter-toolbar-dropdown__inner"
        show={open}
        onToggle={setOpen}
        autoClose={false}
        align="start"
      >
        <Dropdown.Toggle
          as={ReportToolbarFilterDropdownToggle}
          id="reports-filter-period-preset-snapshot"
        >
          {icon}
          <span>{monthLabel}</span>
          <span className="mgc_down_small_fill" aria-hidden />
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="reports-filter-toolbar-dropdown__menu reports-filter-toolbar-dropdown__menu--snapshot-calendar"
          popperConfig={REPORTS_FILTER_DROPDOWN_POPPER}
        >
          <div
            className="reports-filter-toolbar-dropdown__calendar-wrap"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <CalendarComponent
              value={snapDate}
              change={handleCalendarChange}
              showTodayButton
            />
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

type ThisMonthFilterWithDateRangeProps = {
  menu: ReportsToolbarMenuEntry[]
  valueId: string
  displayLabel: string
  icon: React.ReactNode
  selectedByFilterId: Record<string, string>
  selectValue: (filterId: string, valueId: string, label: string) => void
  applySelectionPatch: (
    patch: Record<string, string>,
    filterEvent?: { filterId: string; valueId: string; label: string },
  ) => void
}

function ThisMonthFilterWithDateRange(
  props: ThisMonthFilterWithDateRangeProps,
): React.ReactElement {
  const {
    menu,
    valueId,
    displayLabel,
    icon,
    selectedByFilterId,
    selectValue,
    applySelectionPatch,
  } = props

  const drrInstanceRef = useRef<DateRangePickerComponent | null>(null)

  const today = useMemo(() => {
    const d = new Date()
    d.setHours(12, 0, 0, 0)
    return d
  }, [])

  const quarterAgo = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - 3)
    d.setHours(12, 0, 0, 0)
    return d
  }, [])

  const hasStoredRange = Boolean(
    selectedByFilterId['hist-range-start']?.trim() &&
      selectedByFilterId['hist-range-end']?.trim(),
  )

  const defaultRangeYmd = useMemo(
    () => ({ start: toLocalYmd(quarterAgo), end: toLocalYmd(today) }),
    [quarterAgo, today],
  )

  const startDate = useMemo(
    () =>
      parseYmdToLocalDate(selectedByFilterId['hist-range-start'], quarterAgo),
    [quarterAgo, selectedByFilterId['hist-range-start']],
  )
  const endDate = useMemo(
    () => parseYmdToLocalDate(selectedByFilterId['hist-range-end'], today),
    [selectedByFilterId['hist-range-end'], today],
  )

  const handleDateRangeChange = useCallback(
    (start: Date, end: Date) => {
      applySelectionPatch({
        'hist-range-start': toLocalYmd(start),
        'hist-range-end': toLocalYmd(end),
      })
    },
    [applySelectionPatch],
  )

  const handleItemClick = useCallback(
    (entry: Extract<ReportsToolbarMenuEntry, { type: 'item' }>) => {
      if (entry.id === DATE_RANGE_VALUE_ID) {
        if (!hasStoredRange) {
          applySelectionPatch(
            {
              [THIS_MONTH_ID]: DATE_RANGE_VALUE_ID,
              'hist-range-start': defaultRangeYmd.start,
              'hist-range-end': defaultRangeYmd.end,
            },
            {
              filterId: THIS_MONTH_ID,
              valueId: DATE_RANGE_VALUE_ID,
              label: entry.label,
            },
          )
        } else if (valueId !== DATE_RANGE_VALUE_ID) {
          selectValue(THIS_MONTH_ID, entry.id, entry.label)
        }
        return
      }
      selectValue(THIS_MONTH_ID, entry.id, entry.label)
    },
    [
      applySelectionPatch,
      defaultRangeYmd.end,
      defaultRangeYmd.start,
      hasStoredRange,
      selectValue,
      valueId,
    ],
  )

  const periodPresetOptions = useMemo(() => menuToSelectOptions(menu), [menu])

  const handlePresetChange = useCallback(
    (nextValue: string) => {
      const entry = menu.find(
        (
          candidate,
        ): candidate is Extract<ReportsToolbarMenuEntry, { type: 'item' }> =>
          candidate.type === 'item' && candidate.id === nextValue,
      )
      if (entry) handleItemClick(entry)
    },
    [handleItemClick, menu],
  )

  const openDrrOnFieldClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      const el = e.target instanceof Element ? e.target : null
      if (
        el?.closest('input, button, [class*="e-clear"], .e-input-group-icon')
      ) {
        return
      }
      drrInstanceRef.current?.show?.()
    },
    [],
  )

  if (valueId === DATE_RANGE_VALUE_ID) {
    return (
      <div
        className="reports-filter-toolbar-drr-inline reports-filter-toolbar-dropdown reports-filter-toolbar-dropdown--month-drr"
        role="group"
        aria-label="Date range and period"
      >
        <div
          className="reports-filter-toolbar-drr-inline__drr-input-wrap"
          onClick={openDrrOnFieldClick}
          role="presentation"
        >
          <DateRangePicker
            ref={drrInstanceRef}
            className="reports-filter-toolbar-drr-inline__drr-control"
            width="100%"
            showClearButton
            openOnFocus
            allowEdit
            showDateBtn={false}
            dateRange={{ start: startDate, end: endDate }}
            handleDateRangeChange={handleDateRangeChange}
          />
        </div>
        <div className="reports-filter-toolbar-drr-inline__presets">
          <ReportToolbarSelect
            className="reports-toolbar-select--period-inline"
            value={valueId}
            options={periodPresetOptions}
            onChange={handlePresetChange}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="reports-filter-toolbar-dropdown reports-filter-toolbar-dropdown--month-drr">
      <ReportToolbarSelect
        className="reports-toolbar-select--period"
        value={valueId}
        placeholder={toSentenceCase(displayLabel)}
        options={periodPresetOptions}
        icon={icon}
        onChange={handlePresetChange}
      />
    </div>
  )
}

function ReportFilterToolbar({
  filters,
  initialSelectionByFilterId,
  value,
  onValueChange,
  onFilterChange,
  pageId,
  previewPath = '/preview',
  onReportSaveViewActionChange,
  onReportGeneratePDFActionChange,
  onReportScheduleReportActionChange,
  onReportDownloadPDFActionChange,
  pdfDataScope,
  pdfMetrics,
  onScopeAndMetricsChange,
  customCategories,
  onCustomCategoriesChange,
  customTitle,
  onCustomTitleChange,
  onReportFiltersAndMetricsChange,
  onReportPayloadDataChange,
}: ReportFilterToolbarProps): React.ReactElement {
  const isControlled = value !== undefined
  const loc = useLocation()
  const navigate = useNavigate()

  const [uncontrolledSelection, setUncontrolledSelection] = useState<
    Record<string, string>
  >(() => buildToolbarSelection(filters, initialSelectionByFilterId, undefined))

  const onFiltersDataLoaded = useCallback(
    (
      data: any,
      scope?: 'all' | 'custom',
      metrics?: Record<string, boolean>,
      categories?: Record<string, boolean>,
      title?: string | null,
    ) => {
      if (data) {
        setUncontrolledSelection(data)
      }
      if (scope || metrics) {
        onScopeAndMetricsChange?.(scope, metrics)
      }
      if (categories) {
        onCustomCategoriesChange?.(categories)
      }
      if (title) {
        onCustomTitleChange?.(title)
      } else if (title === null) {
        onCustomTitleChange?.('')
      }
    },
    [],
  )

  const {
    savedViewOptions,
    selectedSavedViewId,
    savedViewsButtonLabel,
    hasSavedViewMenu,
    reportFilters,
    reportMetrics,
    reportPayloadData,
    reportPayloadLoading,
    reportPayloadError,
    providerDropdownOptions,
    onSavedViewSelect,
    updateFilters,
    updatePdfScopeAndMetrics,
    updateCustomCategories,
    updateCustomTitle,
    handleSaveView,
    handleDeleteView,
    handleGeneratePDF,
    handleScheduleReport,
    handleDownloadPDF,
  } = useReportManager(
    onFiltersDataLoaded,
    previewPath === '/preview'
      ? `${location.pathname}${previewPath}`
      : previewPath,
    pageId,
    Boolean(onReportPayloadDataChange || onReportDownloadPDFActionChange),
  )

  useEffect(() => {
    onReportSaveViewActionChange?.(
      <AddViewButton handleSaveView={handleSaveView} />,
    )
    onReportGeneratePDFActionChange?.(
      <GeneratePDFButton handleGeneratePDF={handleGeneratePDF} />,
    )
    onReportScheduleReportActionChange?.(
      <ScheduleReportButton
        handleScheduleReport={handleScheduleReport}
        selectedViewId={selectedSavedViewId}
      />,
    )
    onReportDownloadPDFActionChange?.(
      <DownloadPDFButton handleDownloadPDF={handleDownloadPDF} />,
    )
    onReportFiltersAndMetricsChange?.({
      filters: reportFilters,
      metrics: reportMetrics,
    })
    onReportPayloadDataChange?.(
      reportPayloadData,
      reportPayloadLoading,
      reportPayloadError,
    )
  }, [
    pageId,
    reportFilters,
    reportMetrics,
    reportPayloadData,
    reportPayloadLoading,
    reportPayloadError,
  ])

  const latestDataValue = (isControlled ? value : uncontrolledSelection)?.[
    'period-category'
  ]

  const effectiveFilters = useMemo((): ReportsFilterOption[] => {
    const hasLatest = filters.some((f) => f.id === 'period-category')
    const hasTime = filters.some((f) => f.id === 'snapshot-time')
    if (!hasLatest || hasTime || latestDataValue !== 'snapshot') {
      return filters
    }
    const thisMonthIndex = filters.findIndex((f) => f.id === 'period-preset')
    if (thisMonthIndex >= 0) {
      return [
        ...filters.slice(0, thisMonthIndex + 1),
        SNAPSHOT_TIME_FILTER_OPTION,
        ...filters.slice(thisMonthIndex + 1),
      ]
    }
    const latestDataIndex = filters.findIndex((f) => f.id === 'period-category')
    if (latestDataIndex >= 0) {
      return [
        ...filters.slice(0, latestDataIndex + 1),
        SNAPSHOT_TIME_FILTER_OPTION,
        ...filters.slice(latestDataIndex + 1),
      ]
    }
    return [...filters, SNAPSHOT_TIME_FILTER_OPTION]
  }, [filters, latestDataValue])

  const selectedByFilterId = useMemo(
    () =>
      buildToolbarSelection(
        effectiveFilters,
        initialSelectionByFilterId,
        isControlled ? value : uncontrolledSelection,
      ),
    [
      effectiveFilters,
      initialSelectionByFilterId,
      isControlled,
      value,
      uncontrolledSelection,
    ],
  )

  const monthChipUsesSnapshotCalendar = useMemo(
    () =>
      filters.some((f) => f.id === 'period-category') &&
      latestDataValue === 'snapshot',
    [filters, latestDataValue],
  )

  const selectValue = useCallback(
    (filterId: string, valueId: string, label: string) => {
      const next = { ...selectedByFilterId, [filterId]: valueId }
      if (isControlled) {
        onValueChange?.(next)
      } else {
        setUncontrolledSelection(next)
      }
      onFilterChange?.({ filterId, valueId, label })
      if (
        filterId === 'period-category' &&
        valueId === 'comparison-mode' &&
        isInAppReportBundlePath(loc.pathname)
      ) {
        void navigate(COMPARISON_MODE_PREVIEW_PATH, {
          state: { reportPreviewSelection: next },
        })
      }
    },
    [
      selectedByFilterId,
      isControlled,
      onValueChange,
      onFilterChange,
      loc.pathname,
      navigate,
    ],
  )

  const applySelectionPatch = useCallback(
    (
      patch: Record<string, string>,
      filterEvent?: { filterId: string; valueId: string; label: string },
    ) => {
      const next = { ...selectedByFilterId, ...patch }
      if (isControlled) {
        onValueChange?.(next)
      } else {
        setUncontrolledSelection(next)
      }
      if (filterEvent) {
        onFilterChange?.(filterEvent)
      }
    },
    [selectedByFilterId, isControlled, onValueChange, onFilterChange],
  )

  const thisMonthPeriodId = selectedByFilterId['period-preset'] ?? ''

  useEffect(() => {
    if (monthChipUsesSnapshotCalendar) return
    if (isPeriodIdAllowedForLatestData(thisMonthPeriodId, latestDataValue)) {
      return
    }
    const nextId = getDefaultPeriodForLatestData(latestDataValue)
    if (isControlled) {
      onValueChange?.({ ...selectedByFilterId, 'period-preset': nextId })
    } else {
      setUncontrolledSelection((prev) => ({ ...prev, 'period-preset': nextId }))
    }
  }, [
    monthChipUsesSnapshotCalendar,
    thisMonthPeriodId,
    latestDataValue,
    isControlled,
    onValueChange,
    selectedByFilterId,
  ])

  const setSnapshotAsOf = useCallback(
    (d: Date) => {
      const iso = toLocalYmd(d)
      const next = {
        ...selectedByFilterId,
        'snapshot-as-of': iso,
        'period-preset': 'snapshot',
      }
      if (isControlled) {
        onValueChange?.(next)
      } else {
        setUncontrolledSelection(next)
      }
    },
    [isControlled, onValueChange, selectedByFilterId],
  )

  useEffect(() => {
    if (
      uncontrolledSelection['period-category'] === 'snapshot' &&
      uncontrolledSelection['period-preset'] !== 'snapshot'
    ) {
      setSnapshotAsOf(new Date())
    } else {
      updateFilters(uncontrolledSelection)
    }
  }, [setSnapshotAsOf, uncontrolledSelection, updateFilters])

  useEffect(() => {
    if (pdfDataScope && pdfMetrics) {
      updatePdfScopeAndMetrics({ scope: pdfDataScope, metrics: pdfMetrics })
    }
  }, [pdfDataScope, pdfMetrics, updatePdfScopeAndMetrics])

  useEffect(() => {
    if (customCategories) {
      updateCustomCategories(customCategories)
    }
  }, [customCategories, updateCustomCategories])

  useEffect(() => {
    if (customTitle) {
      updateCustomTitle(customTitle)
    } else {
      updateCustomTitle(null)
    }
  }, [customTitle, updateCustomTitle])

  const menuFor = useCallback(
    (filter: ReportsFilterOption): ReportsToolbarMenuEntry[] =>
      getReportsToolbarMenu(
        filter.id,
        filter.label,
        {
          latestDataValue,
        },
        providerDropdownOptions,
      ),
    [latestDataValue, providerDropdownOptions],
  )

  return (
    <div className="reports-patient-base-health__toolbar">
      <div className="reports-patient-base-health__filters">
        {effectiveFilters.map((filter) => {
          if (filter.id === 'period-preset' && monthChipUsesSnapshotCalendar) {
            return (
              <SnapshotMonthDateFilter
                key="period-preset"
                filterLabel={filter.label}
                icon={getFilterIcon(filter)}
                snapshotAsOfIso={selectedByFilterId['snapshot-as-of'] || ''}
                onSnapshotAsOfChange={setSnapshotAsOf}
              />
            )
          }
          if (filter.id === 'period-preset' && !monthChipUsesSnapshotCalendar) {
            const monthMenu = menuFor(filter)
            const monthValueId =
              selectedByFilterId[filter.id] ??
              REPORTS_TOOLBAR_DEFAULT_SELECTION[filter.id] ??
              filter.id
            const monthDisplayLabel = getReportsToolbarMenuItemLabel(
              filter.id,
              monthValueId,
              filter.label,
              latestDataValue,
            )
            return (
              <ThisMonthFilterWithDateRange
                key={filter.id}
                menu={monthMenu}
                valueId={monthValueId}
                displayLabel={monthDisplayLabel}
                icon={getFilterIcon(filter)}
                selectedByFilterId={selectedByFilterId}
                selectValue={selectValue}
                applySelectionPatch={applySelectionPatch}
              />
            )
          }
          const icon = getFilterIcon(filter)
          const menu = menuFor(filter)
          const valueId =
            selectedByFilterId[filter.id] ??
            REPORTS_TOOLBAR_DEFAULT_SELECTION[filter.id] ??
            filter.id
          const displayLabel = getReportsToolbarMenuItemLabel(
            filter.id,
            valueId,
            filter.label,
            undefined,
            providerDropdownOptions,
          )

          return (
            <ReportToolbarSelect
              key={filter.id}
              className={`reports-toolbar-select--${filter.id}`}
              value={valueId}
              placeholder={toSentenceCase(displayLabel)}
              options={menuToSelectOptions(
                menu,
                filter.id === 'provider-filter' ||
                  filter.id === 'comparison-provider',
              )}
              icon={icon}
              onChange={(nextValue) => {
                const selectedEntry = menu.find(
                  (entry) => entry.type === 'item' && entry.id === nextValue,
                )
                if (selectedEntry?.type === 'item') {
                  selectValue(filter.id, selectedEntry.id, selectedEntry.label)
                }
              }}
            />
          )
        })}
      </div>
      <div className="reports-patient-base-health__saved-views-wrap">
        {hasSavedViewMenu && savedViewOptions && onSavedViewSelect ? (
          <div className="d-flex gap-2">
            <ReportToolbarSelect
              className="reports-toolbar-select--saved-views"
              value={selectedSavedViewId ?? ''}
              placeholder={savedViewsButtonLabel}
              options={savedViewOptions.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
              onChange={onSavedViewSelect}
            />
            {selectedSavedViewId && (
              <>
                <UpdateViewButton
                  handleSaveView={handleSaveView}
                  selectedViewId={selectedSavedViewId}
                />
                <DeleteViewButton
                  handleDeleteView={handleDeleteView}
                  selectedViewId={selectedSavedViewId}
                  selectedViewName={
                    savedViewOptions?.find(
                      (view) => view.id === selectedSavedViewId,
                    )?.name || ''
                  }
                />
              </>
            )}
          </div>
        ) : (
          <ReportToolbarSelect
            className="reports-toolbar-select--saved-views"
            placeholder={savedViewsButtonLabel}
            options={[]}
            disabled
          />
        )}
      </div>
    </div>
  )
}

export default ReportFilterToolbar
